import { useQuery } from "@tanstack/react-query";
import axios from "axios";

export function validateVideoManifest(data) {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid response received from the server.");
  }

  if (data.status === "error") {
    throw new Error(data.message || "Unable to load video(s) for this test case.");
  }

  if (!Array.isArray(data.videos)) {
    throw new Error("No videos were found in the server response.");
  }

  return data.videos.map((video, index) => {
    if (!video || typeof video.url !== "string" || video.url.trim() === "") {
      throw new Error(`Video at position ${index + 1} is missing a valid URL.`);
    }

    return {
      index: typeof video.index === "number" ? video.index : index,
      name:
        typeof video.name === "string" && video.name.trim() !== ""
          ? video.name
          : `Video ${index + 1}`,
      size: typeof video.size === "number" ? video.size : undefined,
      url: video.url,
    };
  });
}

async function fetchVideoManifest(videoUrl) {
  try {
    const response = await axios.get(videoUrl);
    return validateVideoManifest(response.data);
  } catch (err) {
    if (err.response) {
      const status = err.response.status;
      if (status === 404) throw new Error("No recording was found for this test case.");
      if (status === 401 || status === 403)
        throw new Error("You are not authorized to view this recording.");
      if (status >= 500)
        throw new Error("The server had a problem loading this recording. Please try again.");
      throw new Error(err.response.data?.message || "Failed to load the recording.");
    }

    if (err.request) {
      throw new Error("Could not reach the server. Check your connection and try again.");
    }

    throw err;
  }
}

export const useFetchVideoManifest = (videoUrl, options = {}) => {
  const { enabled = true, ...restOptions } = options;

  return useQuery({
    queryKey: ["videoManifest", videoUrl],
    queryFn: () => fetchVideoManifest(videoUrl),
    enabled: !!videoUrl && enabled,
    retry: 2,
    staleTime: 5 * 60 * 1000,
    ...restOptions,
  });
};
